import { useMemo, useState } from "react";

import { cn } from "@/lib/utils";
import { Spinner } from "@fluentui/react-components";
import { Search } from "lucide-react";

import PayoutTable from "./payout-table";

import { useAdminPayoutsQuery } from "@/queries/admin/payouts";

const AdminPayouts = () => {
  const [searchString, setSearchString] = useState("");

  const { adminPayouts, adminPayoutsLoading } = useAdminPayoutsQuery();

  const payouts = useMemo(() => {
    if (!adminPayouts) {
      return [];
    } else if (!searchString) {
      return adminPayouts;
    } else {
      /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
      const filterString = searchString.toLowerCase();
      return adminPayouts.filter(
        (payout) =>
          (payout.company.name ?? "Unnamed")
            .toLowerCase()
            .includes(filterString) ||
          (payout.user.name ?? "Unnamed")
            .toLowerCase()
            .includes(filterString) ||
          (payout.jobOpening.role ?? "Unnamed")
            .toLowerCase()
            .includes(filterString),
      );
      /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */
    }
  }, [adminPayouts, searchString]);

  const count = useMemo(() => payouts?.length, [payouts]);

  if (adminPayoutsLoading) {
    return (
      <div>
        <h3 className="py-4 text-2xl font-semibold group-hover:underline text-left">
          Payouts
        </h3>
        <div className="w-full flex-col justify-around align-middle">
          <Spinner size="huge" />
          <p className="text-center text-base font-medium text-[#727E94]">
            Loading...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h3 className="py-4 text-2xl font-semibold group-hover:underline text-left">
        Payouts
      </h3>
      <div className="w-full flex flex-col gap-4 items-start self-stretch">
        <div className="flex flex-col md:flex-row md:items-center justify-between self-stretch">
          <p className="text-lg text-[#181d27] font-semibold">
            {`${count} ${count === 1 ? "Payout" : "Payouts"}`}
          </p>
          <div className="flex flex-row items-center gap-3">
            <div
              className={cn(
                "flex flex-row items-center gap-0.5",
                "border-b border-[#57576B] transition-colors focus-within:border-black",
              )}
            >
              <Search />
              <input
                type="text"
                className={cn(
                  "flex h-10 w-full min-w-48 px-3 py-2 text-base",
                  "file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
                )}
                placeholder="Search for any payout"
                value={searchString}
                onChange={(ev) => {
                  ev.preventDefault();
                  setSearchString(ev.target.value);
                }}
              />
            </div>
          </div>
        </div>
        <PayoutTable payouts={payouts} />
      </div>
    </div>
  );
};

export default AdminPayouts;
